/**
 * VendorSyncCard.jsx — admin-only card on the Manufacturer Library page.
 *
 * v2.56: MVP UI for the Schneider Partner Product Catalog integration.
 *   • Health strip (mock vs live + last ping detail)
 *   • "Run full sync" + "Run delta since <date>" buttons
 *   • Recent-sync audit table (last 5)
 *
 * All calls scoped to /api/vendors — router enforces org-admin rights.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plug, ArrowsClockwise, Spinner, CheckCircle, XCircle,
} from "@phosphor-icons/react";

function StatusPill({ ok, mode }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${
      ok ? "border-[#00E5FF]/40 text-[#00E5FF] bg-[#00E5FF]/5"
         : "border-[#FF6B6B]/40 text-[#FF6B6B] bg-[#FF6B6B]/5"}`}
          data-testid={`vendor-status-pill-${mode}`}>
      {ok ? <CheckCircle size={11} /> : <XCircle size={11} />}
      {mode}{ok ? " · ok" : " · down"}
    </span>
  );
}

function fmtDur(ms) {
  if (!ms || ms < 1000) return `${ms || 0}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export default function VendorSyncCard() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncs, setSyncs] = useState([]);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vendors");
      setVendors(data.items || []);
      setPermissionDenied(false);
    } catch (e) {
      if (e?.response?.status === 403) setPermissionDenied(true);
      else toast.error(`Vendor list failed: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshSyncs = useCallback(async (slug = "schneider") => {
    try {
      const { data } = await api.get(`/vendors/${slug}/syncs?limit=5`);
      setSyncs(data.items || []);
    } catch { /* silent — history is best-effort */ }
  }, []);

  useEffect(() => { refresh(); refreshSyncs(); }, [refresh, refreshSyncs]);

  const runSync = useCallback(async (slug, since = null) => {
    setSyncing(true);
    try {
      const params = since ? { since } : undefined;
      const { data } = await api.post(`/vendors/${slug}/sync`, null,
                                            params ? { params } : undefined);
      toast.success(
        `${slug} sync ${data.status} · +${data.inserted} new · ${data.updated} updated · ${data.unchanged} unchanged · ${data.errors} errors (${fmtDur(data.duration_ms)})`);
      await refresh();
      await refreshSyncs(slug);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Vendor sync is restricted to org admins.");
        setPermissionDenied(true);
      } else {
        toast.error(`Sync failed: ${e?.response?.data?.detail || e?.message}`);
      }
    } finally {
      setSyncing(false);
    }
  }, [refresh, refreshSyncs]);

  if (permissionDenied) {
    return null;   // Non-admins never see the card
  }

  return (
    <div className="mt-10 border border-white/10 bg-[#12151C] p-5"
         data-testid="vendor-sync-card">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Plug size={16} className="text-[#00E5FF]" />
          <div>
            <div className="label-tag mb-0.5">// admin</div>
            <h2 className="font-display text-xl font-black tracking-tighter">
              Vendor sync
            </h2>
            <p className="text-[11px] text-gray-500 mt-1">
              Live-sync the Manufacturer Library from vendor APIs. Restricted to org admins.
            </p>
          </div>
        </div>
        <button onClick={refresh}
                className="btn-ghost inline-flex items-center gap-1.5 text-xs"
                data-testid="vendor-refresh-btn"
                disabled={loading}>
          <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {loading && vendors.length === 0 ? (
        <div className="text-xs font-mono text-gray-500">Loading vendors…</div>
      ) : vendors.length === 0 ? (
        <div className="text-xs font-mono text-gray-500 border-l-2 border-gray-700 pl-3 py-1">
          No vendors configured.
        </div>
      ) : (
        <ul className="space-y-3">
          {vendors.map((v) => (
            <li key={v.slug}
                className="border border-white/5 bg-[#0F1218] p-3"
                data-testid={`vendor-row-${v.slug}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-mono text-white capitalize">
                    {v.slug}
                  </span>
                  <StatusPill ok={v.ok} mode={v.mode} />
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => runSync(v.slug)}
                          disabled={syncing || !v.ok}
                          className="btn-primary inline-flex items-center gap-1.5 text-[11px] disabled:opacity-40"
                          data-testid={`vendor-sync-full-${v.slug}`}>
                    {syncing ? <Spinner size={11} className="animate-spin" />
                              : <ArrowsClockwise size={11} />}
                    Run full sync
                  </button>
                  <button onClick={() => {
                            const days = window.prompt(
                              "Delta window (days back from now):", "7");
                            if (!days || isNaN(parseInt(days))) return;
                            const d = new Date();
                            d.setDate(d.getDate() - parseInt(days));
                            runSync(v.slug, d.toISOString());
                          }}
                          disabled={syncing || !v.ok}
                          className="btn-ghost inline-flex items-center gap-1.5 text-[11px] disabled:opacity-40"
                          data-testid={`vendor-sync-delta-${v.slug}`}>
                    Δ Delta since…
                  </button>
                </div>
              </div>
              {v.detail && (
                <div className="text-[10px] font-mono text-gray-500 mt-1.5 truncate"
                      title={v.detail}>
                  {v.detail}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Recent runs */}
      <div className="mt-5">
        <div className="label-tag mb-2">// recent runs</div>
        {syncs.length === 0 ? (
          <div className="text-[11px] font-mono text-gray-500 border-l-2 border-gray-700 pl-3 py-1">
            No sync has run yet.
          </div>
        ) : (
          <div className="overflow-x-auto border border-white/5">
            <table className="w-full text-[11px] font-mono"
                    data-testid="vendor-syncs-table">
              <thead className="bg-[#0F1218] text-gray-500 uppercase text-[9px] tracking-wider">
                <tr>
                  <th className="text-left px-2 py-1.5">Started</th>
                  <th className="text-left px-2 py-1.5">Kind</th>
                  <th className="text-left px-2 py-1.5">Status</th>
                  <th className="text-right px-2 py-1.5">+In</th>
                  <th className="text-right px-2 py-1.5">↑Up</th>
                  <th className="text-right px-2 py-1.5">=Un</th>
                  <th className="text-right px-2 py-1.5">!Err</th>
                  <th className="text-right px-2 py-1.5">Dur</th>
                </tr>
              </thead>
              <tbody>
                {syncs.map((s) => (
                  <tr key={s.id}
                      className="border-t border-white/5 hover:bg-white/[0.03]"
                      data-testid={`vendor-sync-row-${s.id}`}>
                    <td className="px-2 py-1 text-gray-300">
                      {new Date(s.started_at).toLocaleString()}
                    </td>
                    <td className="px-2 py-1 text-gray-400">{s.kind}</td>
                    <td className={`px-2 py-1 ${
                      s.status === "ok" ? "text-[#00E5FF]"
                        : s.status === "failed" ? "text-[#FF6B6B]"
                          : "text-[#FFCC00]"}`}>
                      {s.status}
                    </td>
                    <td className="px-2 py-1 text-right text-gray-200">{s.inserted}</td>
                    <td className="px-2 py-1 text-right text-gray-200">{s.updated}</td>
                    <td className="px-2 py-1 text-right text-gray-500">{s.unchanged}</td>
                    <td className={`px-2 py-1 text-right ${
                      s.errors ? "text-[#FF6B6B]" : "text-gray-500"}`}>
                      {s.errors}
                    </td>
                    <td className="px-2 py-1 text-right text-gray-400">
                      {fmtDur(s.duration_ms)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
